import { useLocation, useParams } from 'react-router';
import { AppContent, AppShell, AppSidebarHeader } from '@/components/layout';
import { AdminSidebar } from '@/components/navigation/AdminSidebar';
import { CreateCapabilityDrawer } from '@/components/capabilities/CreateCapabilityDrawer';
import { HeaderActionProvider, useHeaderAction } from '@/lib/header-action-context';
import { getTitleFromRoute } from '@/lib/route-titles';
import { CAPABILITY_TYPE_LABELS } from '@/types';
import { useEffect, useMemo, useState, type PropsWithChildren } from 'react';

function AdminSidebarLayoutContent({ children }: PropsWithChildren) {
    const { pathname, search } = useLocation();
    const params = useParams();
    const { headerAction: contextHeaderAction, headerCenter, headerVersionSelector, headerTitle } = useHeaderAction();
    const typeParam = new URLSearchParams(search).get('type');
    const isAppCapabilityListRoute = /^\/applications\/[^/]+\/capabilities$/.test(pathname);
    const [isCreateDrawerOpen, setCreateDrawerOpen] = useState(false);

    const formatTypeLabel = (type: string): string => {
        if (CAPABILITY_TYPE_LABELS[type]) {
            return CAPABILITY_TYPE_LABELS[type];
        }
        return type
            .replace(/[-_]/g, ' ')
            .replace(/\b\w/g, (letter) => letter.toUpperCase());
    };

    useEffect(() => {
        setCreateDrawerOpen(false);
    }, [pathname, search]);

    const addButton =
        isAppCapabilityListRoute && typeParam ? (
            <button
                type="button"
                onClick={() => setCreateDrawerOpen(true)}
                className="ml-auto inline-flex items-center justify-center rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
                + Add {formatTypeLabel(typeParam)}
            </button>
        ) : null;

    const headerAction = contextHeaderAction ?? addButton;

    const resolvedTitle = useMemo(
        () => headerTitle ?? { title: getTitleFromRoute(pathname) },
        [headerTitle, pathname],
    );

    return (
        <AppShell variant="sidebar">
            <AdminSidebar />
            <AppContent
                variant="sidebar"
                className="min-h-0! flex-1 bg-transparent!"
                style={{
                    display: 'grid',
                    gridTemplateRows: 'auto minmax(0, 1fr)',
                    height: '100dvh',
                    maxHeight: '100dvh',
                    overflow: 'hidden',
                }}
            >
                <AppSidebarHeader headerTitle={resolvedTitle} center={headerCenter} versionSelector={headerVersionSelector}>
                    {headerAction ? headerAction : null}
                </AppSidebarHeader>
                <div className="min-h-0 overflow-auto bg-content-surface">{children}</div>
                {isAppCapabilityListRoute && typeParam && params.appKey && (
                    <CreateCapabilityDrawer
                        open={isCreateDrawerOpen}
                        onOpenChange={setCreateDrawerOpen}
                        appKey={params.appKey}
                        capabilityType={typeParam}
                    />
                )}
            </AppContent>
        </AppShell>
    );
}

export default function AdminSidebarLayout({ children }: PropsWithChildren) {
    return (
        <HeaderActionProvider>
            <AdminSidebarLayoutContent>{children}</AdminSidebarLayoutContent>
        </HeaderActionProvider>
    );
}
